import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import RoundButton from 'components/ui/roundButton';
import breakpoints from 'constants/breakpoints';
import './modal.scss';

/**
 * InlineModal — visually identical to Modal but renders inline in the React
 * tree (no ReactModal portal). Use this when the content inside would trigger
 * other portalled modals and you need both to coexist independently.
 */

interface Props {
  isOpened: boolean;
  onClose: () => void;
  modalId: string;
  customClassName?: string;
  needCloseButton?: boolean;
  onClickAwayClose?: boolean;
  children?: React.ReactNode;
}

const InlineModal = ({
  isOpened,
  onClose,
  modalId,
  customClassName = '',
  needCloseButton,
  onClickAwayClose,
  children,
}: Props) => {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    const nextPortalTarget =
      (host?.closest && host.closest('.layout-app')) ||
      (host?.closest && host.closest('.layout-app__inner')) ||
      (host?.closest && host.closest('.layout-app__wrapper')) ||
      document.querySelector('.layout-app') ||
      document.querySelector('.layout-app__inner') ||
      document.querySelector('.layout-app__wrapper') ||
      host?.parentElement ||
      null;

    setPortalTarget((currentPortalTarget) =>
      currentPortalTarget === nextPortalTarget
        ? currentPortalTarget
        : nextPortalTarget
    );
  }, [isOpened]);

  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      onClickAwayClose &&
      e.target === e.currentTarget &&
      mouseDownTargetRef.current === e.currentTarget
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <>
      <span ref={hostRef} style={{ display: 'none' }} />
      {isOpened && portalTarget
        ? ReactDOM.createPortal(
          <div className='modal-overlay' onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
            <div
              className={`react-modal-portal modal modal-${modalId} ${customClassName}`}
              role='dialog'
              aria-modal='true'>
              {needCloseButton && (
                <RoundButton
                  icon='im-Close'
                  iconSize={10}
                  size={window.innerWidth > breakpoints.panels ? 30 : 24}
                  customClass='modal__close'
                  onClick={onClose}
                />
              )}
              <div className='modal__inner'>{children}</div>
            </div>
          </div>,
          portalTarget
        )
        : null}
    </>
  );
};

export default InlineModal;
